import React, { useEffect, useState } from 'react';

import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ChevronDown, X } from 'lucide-react';

import {
	useGetEnhancedCustomRatesQuery,
	useGetPricingAssumptionsQuery,
	useRecalculateStatementMutation,
} from '../api';
import { useToast } from '../context/ToastContext';
import { useAppDispatch } from '../store';
import { setDirectRates } from '../store/configSlice';
import {
	type AssumptionsForm,
	parseForm,
	sameAssumptions,
	toForm,
} from '../utils/pricingAssumptions';

import {
	qBtn,
	qBtnNeutral,
	qBtnPrimary,
	qInput,
	qLabel,
	qsec,
	qsecHint,
	qsecTitle,
} from './feeStructureStyles';
import PricingAssumptionsSection from './PricingAssumptionsSection';
import QuoteLimitsPanel, {
	QuoteLimitsStatus,
	useQuoteLimits,
} from './QuoteLimitsPanel';

const RATE_PLAN_LABELS: Record<string, string> = {
	IC_PLUS: 'Interchange plus (IC_PLUS)',
	TIERED: 'Tiered',
	FLAT_TIERED: 'Flat tiered',
};

const MODE_LABELS: Record<string, string> = {
	same_for_all: 'Same for all card types',
	different_by_card_type: 'Different by card type',
	different_by_transaction_type: 'Different by transaction type',
	standard: 'Standard',
	custom_assessment: 'Custom assessment rate',
	bundled_amex: 'Bundled Amex rate',
};

// The plan and modes come from the pricing template, so each select shows only its value.
const ReadOnlySelect = ({
	label,
	value,
}: {
	label: string;
	value?: string;
}) => (
	<label className="block">
		<span className={qLabel}>{label}</span>
		<span className="relative block">
			<select
				className={`${qInput} appearance-none pr-10`}
				value={value ?? '—'}
				onChange={() => {}}
			>
				<option>{value ?? '—'}</option>
			</select>
			<ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
		</span>
	</label>
);

// Utility function to format numeric values for form display
// Uses up to 4 decimal places to preserve precision for card brand fees (e.g., APF = 0.0195)
const formatNumericValue = (value: any): string => {
	if (value === null || value === undefined || value === '') return '';
	const num = parseFloat(value);
	if (isNaN(num)) return '';
	return new Intl.NumberFormat('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
		useGrouping: false,
	}).format(num);
};

// Helper function to format all numeric properties in a rate object
const formatRateObject = (
	rates: Record<string, any> | undefined,
): Record<string, string> => {
	if (!rates) return {};
	return Object.fromEntries(
		Object.entries(rates).map(([key, value]) => [
			key,
			formatNumericValue(value),
		]),
	);
};

// Helper function to format standard rate properties (discount_rate and authorization_fee)
const formatStandardRates = (
	rates: { discount_rate?: any; authorization_fee?: any } | undefined,
): { discount_rate: string; authorization_fee: string } => {
	if (!rates) return { discount_rate: '', authorization_fee: '' };
	return {
		discount_rate: formatNumericValue(rates.discount_rate),
		authorization_fee: formatNumericValue(rates.authorization_fee),
	};
};

// Inner input component that can properly use hooks
interface NumericInputFieldProps {
	value: string;
	onChange: (value: string) => void;
	onBlur: () => void;
	name: string;
	inputRef: React.Ref<HTMLInputElement>;
	placeholder?: string;
}

const NumericInputField = ({
	value,
	onChange,
	onBlur,
	name,
	inputRef,
	placeholder = '0.00',
}: NumericInputFieldProps) => {
	const isDeletingRef = React.useRef(false);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' || e.key === 'Delete') {
			// Mark that this change is a deletion so we bypass validation
			isDeletingRef.current = true;
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		if (isDeletingRef.current) {
			onChange(inputValue);
			isDeletingRef.current = false;
			return;
		}
		// Regex allows empty string, a single dot, numbers, and numbers with a trailing dot or up to four decimal places
		// (4 decimal places needed for card brand fees like Visa APF = 0.0195)
		if (/^(\d*\.?\d{0,4})?$/.test(inputValue)) {
			onChange(inputValue);
		}
	};

	return (
		<input
			type="text"
			inputMode="decimal"
			autoComplete="off"
			value={value ?? ''}
			onChange={handleChange}
			onBlur={onBlur}
			onKeyDown={handleKeyDown}
			name={name}
			ref={inputRef}
			placeholder={placeholder}
			className="w-full px-3 py-2 border border-gray-300 rounded-md"
		/>
	);
};

// A reusable numeric input component compatible with React Hook Form
interface ControlledNumericInputProps {
	name: string;
	control: any;
	placeholder?: string;
}

const ControlledNumericInput = ({
	name,
	control,
	placeholder = '0.00',
}: ControlledNumericInputProps) => {
	return (
		<Controller
			name={name}
			control={control}
			render={({ field: { onChange, onBlur, value, name, ref } }) => (
				<NumericInputField
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					name={name}
					inputRef={ref}
					placeholder={placeholder}
				/>
			)}
		/>
	);
};

interface CustomizeFeeStructureModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (data: any) => void;
	configId?: string;
	fileId?: string;
	context?: 'upload' | 'analysis';
	customRatesData?: any;
}

// Helper to deeply convert string numbers to actual numbers for submission
const convertToNumbers = (obj: any): any => {
	if (obj === null || obj === undefined || obj === '') return null;
	if (typeof obj === 'string') {
		const num = parseFloat(obj);
		return isNaN(num) ? obj : num;
	}
	if (Array.isArray(obj)) return obj.map(convertToNumbers);
	if (typeof obj !== 'object') return obj;

	return Object.keys(obj).reduce((acc, key) => {
		acc[key] = convertToNumbers(obj[key]);
		return acc;
	}, {} as any);
};

const CustomizeFeeStructureModal = ({
	isOpen,
	onClose,
	onConfirm,
	configId,
	fileId,
	context = 'analysis',
	customRatesData,
}: CustomizeFeeStructureModalProps) => {
	const dispatch = useAppDispatch();

	const customRateId = customRatesData?.custom_rates_id;
	const effectiveConfigId = customRateId || configId;

	// Check for enhanced format (has rates/rate_plan_type) OR legacy format (has card network keys)
	const hasEnhancedFormat = !!(
		customRatesData &&
		(customRatesData.rates || customRatesData.rate_plan_type)
	);
	const hasLegacyFormat = !!(
		customRatesData &&
		(customRatesData.VISA ||
			customRatesData.MASTERCARD ||
			customRatesData.AMERICAN_EXPRESS ||
			customRatesData.DISCOVER)
	);
	const hasFullStructure = !!(
		customRatesData &&
		!customRateId &&
		(hasEnhancedFormat || hasLegacyFormat)
	);
	const shouldSkipApiCall =
		!isOpen || (!effectiveConfigId && !hasFullStructure);

	const {
		data: enhancedConfigResponse,
		isLoading,
		error,
	} = useGetEnhancedCustomRatesQuery(effectiveConfigId || '', {
		skip: shouldSkipApiCall || hasFullStructure,
	});

	const configData = hasFullStructure
		? customRatesData
		: enhancedConfigResponse?.data || null;
	const [recalculateStatement, { isLoading: isRecalculating }] =
		useRecalculateStatementMutation();
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [approvalReason, setApprovalReason] = useState('');
	const { showToast } = useToast();

	const { data: assumptionsData } = useGetPricingAssumptionsQuery(
		fileId || '',
		{ skip: !isOpen || context !== 'analysis' || !fileId },
	);
	const statementAssumptions = assumptionsData?.statement ?? null;
	const [assumptionsForm, setAssumptionsForm] =
		useState<AssumptionsForm | null>(null);
	useEffect(() => {
		if (!statementAssumptions) return;
		setAssumptionsForm(
			toForm(assumptionsData?.current ?? statementAssumptions),
		);
	}, [assumptionsData, statementAssumptions]);
	const parsedAssumptions = assumptionsForm ? parseForm(assumptionsForm) : null;
	const assumptionsEdited = !!(
		statementAssumptions &&
		parsedAssumptions &&
		!(
			'value' in parsedAssumptions &&
			sameAssumptions(parsedAssumptions.value, statementAssumptions)
		)
	);

	const methods = useForm({
		// resolver: zodResolver(formSchema), // If you have a schema
	});

	const { handleSubmit, control, reset, watch, setValue } = methods;
	const quoteLimits = useQuoteLimits(
		context === 'analysis' ? fileId : undefined,
		watch(),
	);
	const ratePlanType = watch('rate_plan_type');
	const selectedRateTypes = watch('selected_rate_types') || [ratePlanType];
	const pricingMode = watch('pricing_mode');
	const passThroughCosts = watch('pass_through_costs');
	const standardRatesMode = watch('rates.standard_rates.mode');
	const tieredRatesMode = watch('rates.tiered_rates.mode');

	// Check which rate types are selected
	const hasICPlus = selectedRateTypes.includes('IC_PLUS');
	const hasTiered = selectedRateTypes.includes('TIERED');
	const hasFlatTiered = selectedRateTypes.includes('FLAT_TIERED');

	const cardTypes = [
		{
			label: 'Credit',
			key: 'credit',
			card_types: ['VISA', 'MASTERCARD', 'DISCOVER'],
		},
		{
			label: 'Debit',
			key: 'debit',
			card_types: ['VISA_DEBIT', 'MASTERCARD_DEBIT', 'DISCOVER_DEBIT'],
		},
		{
			label: 'American Express',
			key: 'amex',
			card_types: ['AMERICAN_EXPRESS'],
		},
		{ label: 'Pin Debit', key: 'pin_debit', card_types: ['PIN_DEBIT'] },
	];

	const tiers = ['Qualified', 'Mid Qualified', 'Non Qualified'];
	const tierFieldPrefix = (tier: string) => tier.toLowerCase().replace(' ', '');

	useEffect(() => {
		if (isOpen && configData) {
			// Create a deep copy to avoid mutation
			const initialData = JSON.parse(JSON.stringify(configData));

			// --- Data Normalization ---

			// Initialize rates structure if missing
			if (!initialData.rates) {
				initialData.rates = {};
			}

			// Standard Rates: Always populate by_card_type for the form
			if (
				initialData.rates?.standard_rates?.mode === 'same_for_all' &&
				initialData.rates.standard_rates.same_for_all
			) {
				const sameForAllRates = initialData.rates.standard_rates.same_for_all;
				initialData.rates.standard_rates.by_card_type = {};
				cardTypes.forEach(({ card_types }) => {
					card_types.forEach((ct) => {
						initialData.rates.standard_rates.by_card_type[ct] =
							formatStandardRates(sameForAllRates);
					});
				});
			} else if (!initialData.rates.standard_rates) {
				// Initialize empty structure if null
				initialData.rates.standard_rates = { by_card_type: {} };
			}

			// Tiered Rates: Normalize complex structures into a flat by_card_type for the form
			if (initialData.rates?.tiered_rates) {
				const newByCardType: { [key: string]: any } = {};
				const sourceByCardType = initialData.rates.tiered_rates.by_card_type;

				if (
					initialData.rates.tiered_rates.mode === 'same_for_all' &&
					initialData.rates.tiered_rates.same_for_all
				) {
					const { credit_rates, debit_rates } =
						initialData.rates.tiered_rates.same_for_all;
					cardTypes.forEach(({ key, card_types }) => {
						const sourceRates =
							key === 'debit' || key === 'pin_debit'
								? debit_rates
								: credit_rates;
						if (sourceRates) {
							card_types.forEach((ct) => {
								newByCardType[ct] = formatRateObject(sourceRates);
							});
						}
					});
				} else if (
					initialData.rates.tiered_rates.mode === 'different_by_card_type' &&
					sourceByCardType
				) {
					Object.keys(sourceByCardType).forEach((cardTypeKey) => {
						const cardData = sourceByCardType[cardTypeKey];
						if (cardData) {
							if (cardData.tiered) {
								newByCardType[cardTypeKey] = formatRateObject(cardData.tiered);
							} else if (cardData.credit_only) {
								// For card types like VISA, MASTERCARD, DISCOVER, we might need to decide which one to use.
								// Assuming we use credit_only for the 'Credit' group and with_debit for 'Debit' group.
								// The current UI structure doesn't distinguish, so we'll default to credit_only for simplicity.
								// This might need refinement based on business logic.
								newByCardType[cardTypeKey] = formatRateObject(
									cardData.credit_only,
								);
							} else if (cardData.with_debit) {
								// This case is less clear for a unified form field, using credit_only as primary
							} else {
								// It might be flat already
								newByCardType[cardTypeKey] = formatRateObject(cardData);
							}
						}
					});
				}
				initialData.rates.tiered_rates.by_card_type = newByCardType;
			} else if (!initialData.rates.tiered_rates) {
				// Initialize empty structure if null (e.g., for IC_PLUS rate plans)
				initialData.rates.tiered_rates = { by_card_type: {} };
			}

			// Flat Tiered Rates: Initialize by_network structure
			if (
				!initialData.rates.flat_tiered_rates ||
				!initialData.rates.flat_tiered_rates.by_network
			) {
				// Initialize with default structure for flat tiered rates
				initialData.rates.flat_tiered_rates = {
					by_network: {
						VISA: { discount_rate: 2.9, authorization_fee: 0.3 },
						MASTERCARD: { discount_rate: 2.9, authorization_fee: 0.3 },
						AMERICAN_EXPRESS: { discount_rate: 3.75, authorization_fee: 0.3 },
						DISCOVER: { discount_rate: 2.9, authorization_fee: 0.3 },
					},
				};
			} else {
				// Check if all rates are zero and provide sensible defaults
				const by_network = initialData.rates.flat_tiered_rates.by_network;
				const allZeros = Object.values(by_network).every(
					(rates: any) =>
						rates.discount_rate === 0 && rates.authorization_fee === 0,
				);

				if (allZeros) {
					// All rates are zero, provide defaults
					initialData.rates.flat_tiered_rates.by_network = {
						VISA: { discount_rate: 2.9, authorization_fee: 0.3 },
						MASTERCARD: { discount_rate: 2.9, authorization_fee: 0.3 },
						AMERICAN_EXPRESS: { discount_rate: 3.75, authorization_fee: 0.3 },
						DISCOVER: { discount_rate: 2.9, authorization_fee: 0.3 },
					};
				}
			}

			// Build dynamic defaults based on the actual rate plan type
			// Use rate_plan_type from customRatesData (statement's actual type) as primary source
			const statementRateType =
				customRatesData?.rate_plan_type ||
				initialData.rate_plan_type ||
				'IC_PLUS';
			const defaultConfig = {
				rate_plan_type: statementRateType,
				selected_rate_types: initialData.selected_rate_types || [
					statementRateType,
				],
				pricing_mode: {
					discount_rate_mode:
						initialData.pricing_mode?.discount_rate_mode ||
						'different_by_card_type',
					authorization_fee_mode:
						initialData.pricing_mode?.authorization_fee_mode || 'same_for_all',
					pass_through_cost_mode:
						initialData.pricing_mode?.pass_through_cost_mode || 'standard',
				},
				rates: {
					standard_rates: initialData.rates.standard_rates || {
						by_card_type: {},
					},
					tiered_rates: initialData.rates.tiered_rates || { by_card_type: {} },
					flat_tiered_rates: initialData.rates.flat_tiered_rates || {
						by_network: {},
					},
				},
				pass_through_costs: {
					use_custom_assessment_rates:
						initialData.pass_through_costs?.use_custom_assessment_rates ??
						false,
					custom_assessment_rate:
						formatNumericValue(
							initialData.pass_through_costs?.custom_assessment_rate,
						) || '',
					use_bundled_amex_rate:
						initialData.pass_through_costs?.use_bundled_amex_rate ?? false,
					bundled_amex_rate:
						formatNumericValue(
							initialData.pass_through_costs?.bundled_amex_rate,
						) || '',
					// FLAT_TIERED card brand fees pass-through
					pass_through_domestic_card_brand_fees:
						initialData.pass_through_costs
							?.pass_through_domestic_card_brand_fees ??
						initialData.pass_through_config?.FLAT_TIERED?.card_brand_fees
							?.domestic ??
						false,
					pass_through_international_card_brand_fees:
						initialData.pass_through_costs
							?.pass_through_international_card_brand_fees ??
						initialData.pass_through_config?.FLAT_TIERED?.card_brand_fees
							?.international ??
						false,
				},
				monthly_and_batch_fees: {
					monthly_fees: initialData.monthly_and_batch_fees?.monthly_fees || [],
					batch_fee:
						formatNumericValue(initialData.monthly_and_batch_fees?.batch_fee) ||
						'',
					batch_count_per_month:
						initialData.monthly_and_batch_fees?.batch_count_per_month || 30,
				},
			};

			// Set form values - use defaultConfig which already has all the data from initialData
			reset(defaultConfig);
		} else if (!isOpen) {
			reset({}); // Clear form on close
			setSubmitError(null);
		}
	}, [
		isOpen,
		configData,
		reset,
		customRateId,
		hasFullStructure,
		effectiveConfigId,
		customRatesData,
	]);

	const onSubmit = async (formData: any) => {
		setSubmitError(null);
		try {
			const payload = convertToNumbers(formData);

			// --- Data Transformation on Submit ---
			// If mode is 'same_for_all', structure the data accordingly.

			// Standard Rates
			if (payload.rates?.standard_rates?.mode === 'same_for_all') {
				const firstCardType = cardTypes[0].card_types[0];
				payload.rates.standard_rates.same_for_all =
					payload.rates.standard_rates.by_card_type?.[firstCardType] || {};
				payload.rates.standard_rates.by_card_type = null;
			}

			// Tiered Rates
			if (payload.rates?.tiered_rates?.mode === 'same_for_all') {
				const creditCardType = 'VISA'; // Representative for credit
				const debitCardType = 'VISA_DEBIT'; // Representative for debit
				payload.rates.tiered_rates.same_for_all = {
					credit_rates:
						payload.rates.tiered_rates.by_card_type?.[creditCardType] || {},
					debit_rates:
						payload.rates.tiered_rates.by_card_type?.[debitCardType] || {},
					same_as_credit: false, // Assuming this logic or can be made dynamic
				};
				payload.rates.tiered_rates.by_card_type = null;
			} else if (
				payload.rates?.tiered_rates?.mode === 'different_by_card_type'
			) {
				const originalByCardType = payload.rates.tiered_rates.by_card_type;
				const reconstructedByCardType: { [key: string]: any } = {};

				if (originalByCardType) {
					Object.keys(originalByCardType).forEach((cardTypeKey) => {
						const rates = originalByCardType[cardTypeKey];
						// Based on the original structure, some card types have a nested 'tiered' object,
						// while others have 'credit_only' and 'with_debit'.
						// This logic reconstructs that based on card type.
						switch (cardTypeKey) {
							case 'AMERICAN_EXPRESS':
							case 'DISCOVER_DEBIT':
							case 'MASTERCARD_DEBIT':
							case 'PIN_DEBIT':
							case 'VISA_DEBIT':
								reconstructedByCardType[cardTypeKey] = { tiered: rates };
								break;
							case 'DISCOVER':
							case 'MASTERCARD':
							case 'VISA':
								// For these types, the original structure had both credit_only and with_debit.
								// The form simplifies this. We need to decide how to reconstruct.
								// A reasonable assumption is that the form edits the 'credit_only' part,
								// and we might need to fetch or preserve the 'with_debit' part if it exists.
								// For now, we'll reconstruct based on what the form provides.
								reconstructedByCardType[cardTypeKey] = { credit_only: rates };
								break;
							default:
								reconstructedByCardType[cardTypeKey] = rates;
								break;
						}
					});
				}
				payload.rates.tiered_rates.by_card_type = reconstructedByCardType;
			} else if (
				payload.rates?.tiered_rates &&
				!payload.rates.tiered_rates.mode
			) {
				// If tiered_rates exists but has no mode (e.g., IC_PLUS plan), set it to null
				payload.rates.tiered_rates = null;
			}

			// Clean up standard_rates if it's not used (e.g., TIERED-only plan)
			if (payload.rates?.standard_rates && !payload.rates.standard_rates.mode) {
				// If standard_rates exists but has no mode, check if it's empty
				const hasData =
					payload.rates.standard_rates.by_card_type &&
					Object.keys(payload.rates.standard_rates.by_card_type).length > 0;
				if (!hasData) {
					payload.rates.standard_rates = null;
				}
			}

			const directRates = {
				rates: payload.rates,
				pass_through_costs: payload.pass_through_costs,
				monthly_and_batch_fees: payload.monthly_and_batch_fees,
				rate_plan_type: payload.rate_plan_type,
				selected_rate_types: payload.selected_rate_types,
				pricing_mode: payload.pricing_mode,
			};

			if (context === 'upload') {
				dispatch(setDirectRates(directRates as any));
				onConfirm({
					rates: directRates,
					recalculate: false,
					fileId: fileId,
				});
				onClose();
				return;
			}

			if (fileId) {
				if (parsedAssumptions && 'error' in parsedAssumptions) {
					setSubmitError(parsedAssumptions.error);
					return;
				}
				// Extract risk tier from the document's stored results for ISO profit analysis
				const selectedRiskTier =
					customRatesData?.processing_metadata?.selected_risk_tier ||
					customRatesData?.revenue_analysis?.risk_tier;

				const updatedConfig = {
					direct_rates: directRates,
					rerun: false,
					...(selectedRiskTier && { selected_risk_tier: selectedRiskTier }),
					...(approvalReason.trim() && {
						approval_reason: approvalReason.trim(),
					}),
					...(parsedAssumptions && {
						assumptions:
							assumptionsEdited && 'value' in parsedAssumptions
								? parsedAssumptions.value
								: null,
					}),
				};
				const result = await recalculateStatement({
					fileId,
					config: updatedConfig,
				}).unwrap();
				if (result.approval_request) {
					showToast(
						'Sent for approval',
						'The quote is outside its pricing limits. The current rates stay until an approver approves the new ones.',
						'info',
					);
				}
				onConfirm({
					fileId,
					configId,
					rates: updatedConfig.direct_rates.rates,
					pass_through_costs: updatedConfig.direct_rates.pass_through_costs,
					success: result.success,
					recalculated: true,
				});
			}
			onClose();
		} catch (err) {
			console.error('Failed to recalculate statement:', err);
			setSubmitError(
				(err as any)?.data?.message ||
					'Could not apply the changes right now. Please try again in a moment.',
			);
		}
	};

	if (!isOpen) return null;

	if (isLoading) {
		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
				<div className="bg-white rounded-lg p-6">Loading configuration...</div>
			</div>
		);
	}

	if (error || (!effectiveConfigId && !hasFullStructure)) {
		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
				<div className="bg-white rounded-lg p-6 text-center">
					<div className="text-red-600">
						{error
							? `Failed to load rates: ${'message' in error ? (error as any).message : 'Unknown error'}`
							: 'No configuration data provided'}
					</div>
					<button
						onClick={onClose}
						className="mt-4 px-4 py-2 bg-gray-500 text-white rounded"
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	return (
		<FormProvider {...methods}>
			<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-6">
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="w-full max-w-[1280px] h-[calc(100vh-48px)] bg-white rounded-lg shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1)] flex flex-col"
				>
					<div className="flex-shrink-0 p-6 flex justify-between items-center border-b border-gray-200">
						<div>
							<h4 className="text-xl font-semibold text-gray-900">
								Customize Fee Structure
							</h4>
							<div className="text-xs text-gray-500">
								{[configData?.name, 'changes apply to this statement only']
									.filter(Boolean)
									.join(' · ')}
							</div>
						</div>
						{quoteLimits && (
							<span className="ml-auto mr-3">
								<QuoteLimitsStatus limits={quoteLimits} />
							</span>
						)}
						<button
							type="button"
							onClick={onClose}
							aria-label="Close"
							className="w-8 h-8 flex items-center justify-center rounded-md text-primary-500"
						>
							<X className="w-4 h-4" />
						</button>
					</div>

					<div className="flex-1 min-h-0 overflow-auto px-7 py-6 flex flex-col gap-6">
						{!configData ? (
							<div className="p-6 text-center text-gray-600">
								Loading configuration...
							</div>
						) : (
							<>
								<section className={qsec}>
									<h5 className={qsecTitle}>Rate plan type</h5>
									<p className={qsecHint}>
										From the pricing template. Change the template to change the
										plan.
									</p>
									<div className="grid grid-cols-2 gap-4">
										<ReadOnlySelect
											label="Rate plan type"
											value={RATE_PLAN_LABELS[ratePlanType] ?? ratePlanType}
										/>
									</div>
									<h5 className={`${qsecTitle} mt-4`}>Pricing mode</h5>
									<div className="grid grid-cols-3 gap-4">
										<ReadOnlySelect
											label="Discount rate"
											value={MODE_LABELS[pricingMode?.discount_rate_mode]}
										/>
										<ReadOnlySelect
											label="Authorization fee"
											value={MODE_LABELS[pricingMode?.authorization_fee_mode]}
										/>
										<ReadOnlySelect
											label="Pass-through cost"
											value={MODE_LABELS[pricingMode?.pass_through_cost_mode]}
										/>
									</div>
								</section>

								{statementAssumptions && assumptionsForm && (
									<PricingAssumptionsSection
										form={assumptionsForm}
										edited={assumptionsEdited}
										onChange={setAssumptionsForm}
										onReset={() =>
											setAssumptionsForm(toForm(statementAssumptions))
										}
									/>
								)}

								{quoteLimits && (
									<QuoteLimitsPanel
										limits={quoteLimits}
										reason={approvalReason}
										onReason={setApprovalReason}
									/>
								)}

								{/* Standard Rates */}
								{hasICPlus && (
									<div className="p-6">
										<h3 className="text-lg font-medium mb-4">Standard Rates</h3>
										{standardRatesMode === 'same_for_all' ? (
											// Simplified view for same_for_all mode
											<div className="bg-white rounded-lg border border-gray-200 p-6">
												<div className="grid grid-cols-2 gap-6">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-2">
															Discount Rate (%)
														</label>
														<ControlledNumericInput
															name={`rates.standard_rates.by_card_type.${cardTypes[0].card_types[0]}.discount_rate`}
															control={control}
														/>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-2">
															Authorization Fee ($)
														</label>
														<ControlledNumericInput
															name={`rates.standard_rates.by_card_type.${cardTypes[0].card_types[0]}.authorization_fee`}
															control={control}
														/>
													</div>
												</div>
												<p className="text-xs text-gray-500 mt-3">
													These rates apply to all card types
												</p>
											</div>
										) : (
											// Full table view for different_by_card_type mode
											<table className="w-full text-sm">
												<thead>
													<tr className="bg-neutral-100">
														<th className="text-left px-4 py-3 font-semibold">
															Card Type
														</th>
														<th className="text-center px-4 py-3 font-semibold">
															Rate (%)
														</th>
														<th className="text-center px-4 py-3 font-semibold">
															Auth Fee ($)
														</th>
													</tr>
												</thead>
												<tbody>
													{cardTypes.map(({ label, card_types }) => (
														<tr
															key={label}
															className="border-b hover:bg-gray-50"
														>
															<td className="px-4 py-2 font-medium">{label}</td>
															<td className="px-4 py-2">
																<ControlledNumericInput
																	name={`rates.standard_rates.by_card_type.${card_types[0]}.discount_rate`}
																	control={control}
																/>
															</td>
															<td className="px-4 py-2">
																<ControlledNumericInput
																	name={`rates.standard_rates.by_card_type.${card_types[0]}.authorization_fee`}
																	control={control}
																/>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										)}
									</div>
								)}

								{/* Flat Tiered Rates */}
								{hasFlatTiered && (
									<div className="p-6">
										<h3 className="text-lg font-medium mb-4">
											Flat Tiered Transaction Rates
										</h3>
										<p className="text-sm text-gray-600 mb-4">
											Configure discount rate and authorization fee for each
											card network
										</p>
										<table className="w-full text-sm">
											<thead>
												<tr className="bg-neutral-100">
													<th className="text-left px-4 py-3 font-semibold">
														Card Network
													</th>
													<th className="text-center px-4 py-3 font-semibold">
														Discount Rate (%)
													</th>
													<th className="text-center px-4 py-3 font-semibold">
														Authorization Fee ($)
													</th>
												</tr>
											</thead>
											<tbody>
												{[
													{ label: 'Visa', key: 'VISA' },
													{ label: 'Mastercard', key: 'MASTERCARD' },
													// Canonical key — configs/backend store flat AMEX under
													// AMERICAN_EXPRESS; using "AMEX" here read/wrote the wrong
													// slot, so the stored AMEX rate showed as 0.00.
													{ label: 'American Express', key: 'AMERICAN_EXPRESS' },
													{ label: 'Discover', key: 'DISCOVER' },
												].map(({ label, key }) => (
													<tr key={key} className="border-b hover:bg-gray-50">
														<td className="px-4 py-2 font-medium">{label}</td>
														<td className="px-4 py-2">
															<ControlledNumericInput
																name={`rates.flat_tiered_rates.by_network.${key}.discount_rate`}
																control={control}
															/>
														</td>
														<td className="px-4 py-2">
															<ControlledNumericInput
																name={`rates.flat_tiered_rates.by_network.${key}.authorization_fee`}
																control={control}
															/>
														</td>
													</tr>
												))}
											</tbody>
										</table>

										{/* Card Brand Fees Pass-through */}
										<div className="mt-6">
											<h4 className="text-base font-medium mb-3">
												Card Brand Fees Pass-through
											</h4>
											<p className="text-sm text-gray-600 mb-4">
												When enabled, card brand fees will be added to the
												proposed fees (revenue-neutral pass-through)
											</p>
											<div className="space-y-3 bg-gray-50 p-4 rounded-lg">
												<Controller
													name="pass_through_costs.pass_through_domestic_card_brand_fees"
													control={control}
													render={({ field }) => (
														<label className="flex items-center justify-between cursor-pointer">
															<div>
																<span className="font-medium">
																	Domestic Card Brand Fees
																</span>
																<p className="text-xs text-gray-500">
																	~0.20% base rate for domestic transactions
																</p>
															</div>
															<input
																type="checkbox"
																{...field}
																checked={field.value || false}
																className="rounded"
															/>
														</label>
													)}
												/>
												<Controller
													name="pass_through_costs.pass_through_international_card_brand_fees"
													control={control}
													render={({ field }) => (
														<label className="flex items-center justify-between cursor-pointer">
															<div>
																<span className="font-medium">
																	International Card Brand Fees
																</span>
																<p className="text-xs text-gray-500">
																	~1.45% (V/MC/D) or ~1.0% (AMEX) + per-txn fees
																</p>
															</div>
															<input
																type="checkbox"
																{...field}
																checked={field.value || false}
																className="rounded"
															/>
														</label>
													)}
												/>
											</div>
										</div>
									</div>
								)}

								{/* Tiered Rates */}
								{hasTiered && (
									<div className="p-6 bg-blue-50 border-t">
										<h3 className="text-lg font-medium mb-4">Tiered Rates</h3>
										{tieredRatesMode === 'same_for_all'
											? // Simplified view for same_for_all mode - only show Credit and Debit
												tiers.map((tier) => (
													<div key={tier} className="mb-6">
														<h4 className="font-normal text-base mb-3">
															{tier}
														</h4>
														<table className="w-full text-sm bg-white rounded-lg overflow-hidden">
															<thead>
																<tr className="bg-neutral-100">
																	<th className="text-left px-4 py-3 font-semibold">
																		Type
																	</th>
																	<th className="text-center px-4 py-3 font-semibold">
																		Rate (%)
																	</th>
																	<th className="text-center px-4 py-3 font-semibold">
																		Per Item Fee ($)
																	</th>
																</tr>
															</thead>
															<tbody>
																<tr className="border-b hover:bg-gray-50">
																	<td className="px-4 py-2 font-medium">
																		Credit Cards
																	</td>
																	<td className="px-4 py-2">
																		<ControlledNumericInput
																			name={`rates.tiered_rates.by_card_type.${cardTypes[0].card_types[0]}.${tierFieldPrefix(tier)}_rate`}
																			control={control}
																		/>
																	</td>
																	<td className="px-4 py-2">
																		<ControlledNumericInput
																			name={`rates.tiered_rates.by_card_type.${cardTypes[0].card_types[0]}.${tierFieldPrefix(tier)}_per_item`}
																			control={control}
																		/>
																	</td>
																</tr>
																<tr className="border-b hover:bg-gray-50">
																	<td className="px-4 py-2 font-medium">
																		Debit Cards
																	</td>
																	<td className="px-4 py-2">
																		<ControlledNumericInput
																			name={`rates.tiered_rates.by_card_type.${cardTypes[1].card_types[0]}.${tierFieldPrefix(tier)}_rate`}
																			control={control}
																		/>
																	</td>
																	<td className="px-4 py-2">
																		<ControlledNumericInput
																			name={`rates.tiered_rates.by_card_type.${cardTypes[1].card_types[0]}.${tierFieldPrefix(tier)}_per_item`}
																			control={control}
																		/>
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												))
											: // Full table view for different_by_card_type mode
												tiers.map((tier) => (
													<div key={tier} className="mb-6">
														<h4 className="font-normal text-base mb-3">
															{tier}
														</h4>
														<table className="w-full text-sm">
															<thead>
																<tr className="bg-neutral-100">
																	<th className="text-left px-4 py-3 font-semibold">
																		Card Type
																	</th>
																	<th className="text-center px-4 py-3 font-semibold">
																		Rate (%)
																	</th>
																	<th className="text-center px-4 py-3 font-semibold">
																		Per Item Fee ($)
																	</th>
																</tr>
															</thead>
															<tbody>
																{cardTypes.map(({ label, card_types }) => (
																	<tr
																		key={label}
																		className="border-b hover:bg-gray-50"
																	>
																		<td className="px-4 py-2 font-medium">
																			{label}
																		</td>
																		<td className="px-4 py-2">
																			<ControlledNumericInput
																				name={`rates.tiered_rates.by_card_type.${card_types[0]}.${tierFieldPrefix(tier)}_rate`}
																				control={control}
																			/>
																		</td>
																		<td className="px-4 py-2">
																			<ControlledNumericInput
																				name={`rates.tiered_rates.by_card_type.${card_types[0]}.${tierFieldPrefix(tier)}_per_item`}
																				control={control}
																			/>
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												))}
									</div>
								)}

								{/* Pass-through Costs */}
								<div className="p-6 border-t space-y-6">
									<div>
										<h3 className="text-base font-medium mb-3">
											Assessment Fees
										</h3>
										<div className="p-3 bg-gray-50 rounded-lg">
											<Controller
												name="pass_through_costs.use_custom_assessment_rates"
												control={control}
												render={({ field }) => (
													<label className="flex items-center justify-between cursor-pointer">
														<span>Use Custom Assessment Rates</span>
														<input
															type="checkbox"
															{...field}
															checked={field.value}
															className="rounded"
														/>
													</label>
												)}
											/>
											{passThroughCosts?.use_custom_assessment_rates && (
												<div className="mt-3 pt-3 border-t flex items-center justify-between">
													<span className="text-sm text-neutral-600">
														Custom Rate (%)
													</span>
													<div className="w-32">
														<ControlledNumericInput
															name="pass_through_costs.custom_assessment_rate"
															control={control}
														/>
													</div>
												</div>
											)}
										</div>
									</div>
									<div>
										<h3 className="text-base font-medium mb-3">
											Special Rate for American Express
										</h3>
										<div className="p-3 bg-gray-50 rounded-lg">
											<Controller
												name="pass_through_costs.use_bundled_amex_rate"
												control={control}
												render={({ field }) => (
													<label className="flex items-center justify-between cursor-pointer">
														<span>Use Bundled Amex Rate</span>
														<input
															type="checkbox"
															{...field}
															checked={field.value}
															className="rounded"
														/>
													</label>
												)}
											/>
											{passThroughCosts?.use_bundled_amex_rate && (
												<div className="mt-3 pt-3 border-t flex items-center justify-between">
													<span className="text-sm text-neutral-600">
														Bundled Rate (%)
													</span>
													<div className="w-32">
														<ControlledNumericInput
															name="pass_through_costs.bundled_amex_rate"
															control={control}
														/>
													</div>
												</div>
											)}
										</div>
									</div>
								</div>

								{/* Monthly & Batch Fees Section */}
								<div className="bg-white p-6 border-b">
									<h3 className="text-base font-semibold text-neutral-900 mb-4">
										Monthly & Batch Fees
									</h3>
									<div className="space-y-4">
										{/* Monthly Fees */}
										<div>
											<label className="block text-sm font-medium text-neutral-700 mb-2">
												Monthly Fees
											</label>
											<Controller
												name="monthly_and_batch_fees.monthly_fees"
												control={control}
												render={({ field: { value, onChange } }) => {
													const fees = value || [];
													return (
														<div className="space-y-2">
															{fees.map((fee: any, index: number) => (
																<div
																	key={index}
																	className="flex items-center gap-2"
																>
																	<input
																		type="text"
																		placeholder="Label (e.g., PCI Fee)"
																		value={fee.label || ''}
																		onChange={(e) => {
																			const updated = [...fees];
																			updated[index] = {
																				...updated[index],
																				label: e.target.value,
																			};
																			onChange(updated);
																		}}
																		className="flex-1 px-3 py-2 border border-neutral-300 rounded-md text-sm"
																	/>
																	<input
																		type="text"
																		inputMode="decimal"
																		placeholder="Amount"
																		value={
																			fee.amount !== undefined &&
																			fee.amount !== null
																				? fee.amount
																				: ''
																		}
																		onChange={(e) => {
																			const inputValue = e.target.value;
																			if (
																				/^(\d*\.?\d{0,2})?$/.test(inputValue)
																			) {
																				const updated = [...fees];
																				updated[index] = {
																					...updated[index],
																					amount:
																						inputValue === ''
																							? ''
																							: parseFloat(inputValue) || 0,
																				};
																				onChange(updated);
																			}
																		}}
																		className="w-28 px-3 py-2 border border-neutral-300 rounded-md text-sm"
																	/>
																	<button
																		type="button"
																		onClick={() =>
																			onChange(
																				fees.filter(
																					(_: any, i: number) => i !== index,
																				),
																			)
																		}
																		className="px-3 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 text-sm"
																	>
																		Remove
																	</button>
																</div>
															))}
															<button
																type="button"
																onClick={() =>
																	onChange([...fees, { label: '', amount: 0 }])
																}
																className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm"
															>
																+ Add Monthly Fee
															</button>
															{fees.length > 0 && (
																<p className="text-xs text-neutral-500 mt-1">
																	Total: $
																	{fees
																		.reduce(
																			(sum: number, f: any) =>
																				sum + (parseFloat(f.amount) || 0),
																			0,
																		)
																		.toFixed(2)}
																	/month
																</p>
															)}
														</div>
													);
												}}
											/>
										</div>
										{/* Batch Fee */}
										<div>
											<label className="block text-sm font-medium text-neutral-700 mb-2">
												Batch Fee
											</label>
											<div className="flex items-center gap-2">
												<span className="text-sm text-neutral-600">$</span>
												<ControlledNumericInput
													name="monthly_and_batch_fees.batch_fee"
													control={control}
													placeholder="0.00"
												/>
												<span className="text-sm text-neutral-600">
													per batch ×
												</span>
												<Controller
													name="monthly_and_batch_fees.batch_count_per_month"
													control={control}
													render={({ field: { value, onChange } }) => (
														<input
															type="number"
															value={value || 30}
															onChange={(e) =>
																onChange(parseInt(e.target.value) || 30)
															}
															min="1"
															max="365"
															className="w-20 px-3 py-2 border border-neutral-300 rounded-md text-sm"
														/>
													)}
												/>
												<span className="text-sm text-neutral-600">
													batches/month
												</span>
											</div>
											<Controller
												name="monthly_and_batch_fees"
												control={control}
												render={({ field: { value } }) => {
													const batchFee = parseFloat(value?.batch_fee) || 0;
													const batchCount =
														parseInt(value?.batch_count_per_month) || 30;
													const total = batchFee * batchCount;
													return total > 0 ? (
														<p className="text-xs text-neutral-500 mt-1">
															Total: ${total.toFixed(2)}/month
														</p>
													) : null;
												}}
											/>
										</div>
									</div>
								</div>
							</>
						)}
					</div>

					<div className="flex-shrink-0 border-t border-gray-200">
						{submitError && (
							<div className="px-6 pt-4">
								<div
									role="alert"
									className="rounded-lg border border-red-100 bg-red-50 text-red-700 px-4 py-3 text-sm"
								>
									{submitError}
								</div>
							</div>
						)}
						<div className="p-6 flex justify-end gap-3">
							<span className="text-xs text-gray-500 self-center">
								Customizing the fee structure applies to this statement only.
								Your templates do not change.
							</span>
							<span className="ml-auto" />
							<button
								type="button"
								onClick={onClose}
								className={`${qBtn} ${qBtnNeutral}`}
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={isRecalculating}
								className={`${qBtn} ${qBtnPrimary}`}
							>
								{isRecalculating
									? 'Recalculating...'
									: context === 'upload'
										? 'Save & Continue'
										: quoteLimits?.violations.length
											? quoteLimits.data.action === 'block'
												? 'Save'
												: 'Save & request approval'
											: 'Save & recalculate'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</FormProvider>
	);
};

export default CustomizeFeeStructureModal;
